import React, { useState, useEffect } from 'react';
import { HashLink } from 'react-router-hash-link';
import Logo from '../../images/logo.svg';
import solar from '../../images/navbar/solar.jpg';
import pumpe from '../../images/navbar/pumpe.webp';
import wallbox from '../../images/navbar/wallbox.webp';
import energie from '../../images/navbar/energie.webp';
import speicher from '../../images/navbar/speicher.jpg';
import notstrom from '../../images/navbar/gateway.png'; // Added notstrom image

const Button = ({ label, destination, className }) => {
    return (
        <HashLink
            to={destination}
            smooth
            className={`bg-primary text-white rounded-lg font-semibold text-sm px-4 py-2 lg:px-6 lg:py-3 transition-all duration-200 hover:scale-105 inline-flex items-center justify-center whitespace-nowrap ${className}`}
        >
            {label}
        </HashLink>
    );
};

const Navbar = () => {
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProductsOpen, setIsProductsOpen] = useState(false);
    const [isServicesOpen, setIsServicesOpen] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const isScrollingUp = currentScrollY < lastScrollY;

            // Update navbar visibility
            setIsVisible(isScrollingUp || currentScrollY <= 10);

            // Close mobile menu and sections on any scroll
            if (isMobileView) {
                setIsMobileMenuOpen(false);
                setIsProductsOpen(false);
                setIsServicesOpen(false);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY, isMobileView]);

    useEffect(() => {
        const checkMobileView = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobileView(mobile);
            if (!mobile) {
                setIsMobileMenuOpen(false);
                setIsProductsOpen(false);
                setIsServicesOpen(false);
            }
        };

        window.addEventListener('resize', checkMobileView);
        checkMobileView();
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);

    const productLinks = [
        {
            name: 'Solarmodule',
            to: '/solarmodule',
            image: solar
        },
        {
            name: 'Wallbox',
            to: '/wallbox',
            image: wallbox
        },
        {
            name: 'Stromspeicher',
            to: '/stromspeicher',
            image: speicher
        },
        {
            name: 'Notstrom',
            to: '/notstrom',
            image: notstrom
        },
        {
            name: 'Energiemanagement',
            to: '/energiemanagement',
            image: energie
        },
        {
            name: 'Wärmepumpe',
            to: '/waermepumpe',
            image: pumpe
        }
    ];

    const serviceLinks = [
        { name: 'Sanierungsfahrplan', to: '/sanierungsfahrplan' },
        { name: 'Förderrechner Wärmepumpe', to: '/foerderrechner-waermepumpe' },
        { name: 'Förderrechner Fenster, Dach, Fassade', to: '/foerderrechner-fenster-dach-fassade' }
    ];

    const navLinks = [
        { name: 'Produkte', isProductsCategory: true },
        { name: 'Über uns', to: '/ueber-uns' },
        { name: 'Leistungen', isServicesCategory: true },
        { name: 'Kundenrezensionen', to: '/kundenrezensionen' },
        { name: 'In 5 Schritten zur Solaranlage', to: '/solaranlage-schritte' }
    ];

    const renderNavLinks = (isMobile = false) => (
        <ul className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center space-y-4 lg:space-y-0 lg:space-x-6`}>
            {navLinks.map((link, index) => (
                <li key={index} className="relative w-full">
                    {link.isProductsCategory ? (
                        <>
                            <button
                                className={`w-full px-4 py-2 font-semibold text-secondary-dark hover:text-primary text-left lg:text-center whitespace-nowrap`}
                                onClick={() => {
                                    if (isMobile) {
                                        setIsProductsOpen(!isProductsOpen);
                                        setIsServicesOpen(false);
                                    } else {
                                        setIsProductsOpen(!isProductsOpen);
                                        setIsServicesOpen(false);
                                    }
                                }}
                                onMouseEnter={() => {
                                    if (!isMobile) {
                                        setIsProductsOpen(true);
                                        setIsServicesOpen(false);
                                    }
                                }}
                                onMouseLeave={() => {
                                    if (!isMobile) {
                                        // Don't close immediately to allow moving to the dropdown
                                    }
                                }}
                            >
                                <span className="flex justify-between items-center">
                                    <span>{link.name}</span>
                                    {isMobile && (
                                        <span className="ml-2">
                                            {isProductsOpen ? '−' : '+'}
                                        </span>
                                    )}
                                </span>
                            </button>

                            {/* Products section for mobile */}
                            {isMobile && isProductsOpen && (
                                <div className="px-2 pt-3 pb-1 space-y-2">
                                    {productLinks.map((product, productIndex) => (
                                        <HashLink
                                            key={productIndex}
                                            to={product.to}
                                            smooth
                                            className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-primary hover:bg-white transition-all duration-200 shadow-sm"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            <div className="w-14 h-11 mr-3 overflow-hidden rounded-md flex-shrink-0">
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-medium text-gray-900 text-sm leading-tight whitespace-normal break-words">
                                                    {product.name}
                                                </h3>
                                            </div>
                                        </HashLink>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : link.isServicesCategory ? (
                        <>
                            <button
                                className={`w-full px-4 py-2 font-semibold text-secondary-dark hover:text-primary text-left lg:text-center whitespace-nowrap`}
                                onClick={() => {
                                    if (isMobile) {
                                        setIsServicesOpen(!isServicesOpen);
                                        setIsProductsOpen(false);
                                    } else {
                                        setIsServicesOpen(!isServicesOpen);
                                        setIsProductsOpen(false);
                                    }
                                }}
                                onMouseEnter={() => {
                                    if (!isMobile) {
                                        setIsServicesOpen(true);
                                        setIsProductsOpen(false);
                                    }
                                }}
                                onMouseLeave={() => {
                                    if (!isMobile) {
                                        // Don't close immediately to allow moving to the dropdown
                                    }
                                }}
                            >
                                <span className="flex justify-between items-center">
                                    <span>{link.name}</span>
                                    {isMobile && (
                                        <span className="ml-2">
                                            {isServicesOpen ? '−' : '+'}
                                        </span>
                                    )}
                                </span>
                            </button>

                            {/* Services section for mobile */}
                            {isMobile && isServicesOpen && (
                                <div className="px-2 pt-3 pb-1">
                                    <ul className="space-y-1">
                                        {serviceLinks.map((service, serviceIndex) => (
                                            <li key={serviceIndex}>
                                                <HashLink
                                                    to={service.to}
                                                    smooth
                                                    className="block px-3 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 text-sm rounded-md transition-colors duration-200"
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    {service.name}
                                                </HashLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ) : link.subLinks ? (
                        <>
                            <HashLink
                                to={link.to || '#'}
                                smooth
                                className={`px-4 py-2 font-semibold text-secondary-dark hover:text-primary flex justify-between items-center w-full ${
                                    isMobile ? 'text-left' : 'text-center'
                                }`}
                            >
                                <span className="flex-1">{link.name}</span>
                                {isMobile && (
                                    <span className="ml-2">
                                        +
                                    </span>
                                )}
                            </HashLink>

                            {/* Submenu for mobile */}
                            {isMobile && (
                                <div className="px-2 pt-3 pb-1">
                                    <ul className="space-y-1">
                                        {link.subLinks.map((subLink, subIndex) => (
                                            <li key={subIndex}>
                                                <HashLink
                                                    to={subLink.to}
                                                    smooth
                                                    className="block px-3 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 text-sm rounded-md transition-colors duration-200"
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    {subLink.name}
                                                </HashLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ) : (
                        <HashLink
                            to={link.to}
                            smooth
                            className={`px-4 py-2 font-semibold text-secondary-dark hover:text-primary block whitespace-nowrap ${
                                isMobile ? 'text-left' : 'text-center'
                            }`}
                            onClick={() => {
                                if (isMobile) {
                                    setIsMobileMenuOpen(false);
                                }
                            }}
                        >
                            {link.name}
                        </HashLink>
                    )}
                </li>
            ))}
            <li className={`${isMobile ? 'mt-4 w-full' : 'lg:ml-4'}`}>
                <Button
                    label="Beratung vereinbaren"
                    destination="/kontakt"
                    className={isMobile ? 'w-full' : ''}
                />
            </li>
        </ul>
    );

    // Determine navbar background based on page and scroll position
    const getNavbarBackground = () => {
        if (isMobileView) {
            return 'bg-white shadow-lg';
        }

        // On all other pages, always show background
        return 'bg-white shadow-lg';
    };

    return (
        <>
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ease-in-out
                ${isVisible ? 'translate-y-0' : '-translate-y-full'}
                ${getNavbarBackground()}`}>
                <div className="container mx-auto flex justify-between items-center py-2 px-4">
                    {/* Mobile Logo */}
                    <HashLink smooth to="/" className="lg:hidden z-50 relative">
                        <img src={Logo} alt="Logo" className="h-12 w-auto" />
                    </HashLink>

                    {/* Centered Logo and Desktop Navigation */}
                    <div className="hidden lg:flex flex-1 justify-center items-center space-x-3">
                        <HashLink smooth to="/" className="z-50 relative">
                            <img src={Logo} alt="Logo" className="h-16 w-auto" />
                        </HashLink>
                        {renderNavLinks()}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="lg:hidden mobile-menu-button p-2 z-50 relative"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {isMobileMenuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>

                    {/* Mobile Navigation */}
                    <div className={`lg:hidden mobile-menu absolute top-full left-0 w-full bg-white shadow-lg transition-all duration-300 overflow-hidden z-40
                        ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="px-4 py-6">
                            {renderNavLinks(true)}
                        </div>
                    </div>
                </div>

                {/* Desktop Products Section - Full Width */}
                {!isMobileView && isProductsOpen && (
                    <div
                        className="w-full bg-white shadow-lg z-40 border-t border-gray-200"
                        onMouseEnter={() => setIsProductsOpen(true)}
                        onMouseLeave={() => setIsProductsOpen(false)}
                    >
                        <div className="w-full px-4 py-8">
                            <div className="grid grid-cols-6 gap-4">
                                {productLinks.map((product, index) => (
                                    <div key={index} className="col-span-1">
                                        <HashLink
                                            to={product.to}
                                            smooth
                                            className="group block rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-200 flex-shrink-0 bg-white h-full"
                                            onClick={() => {
                                                setIsProductsOpen(false);
                                            }}
                                        >
                                            <div className="w-full overflow-hidden rounded-lg">
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="w-full h-64 object-contain"
                                                />
                                            </div>
                                            <h3 className="font-bold text-gray-900 group-hover:text-primary text-xl text-center py-4 whitespace-normal break-words">
                                                {product.name}
                                            </h3>
                                        </HashLink>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Desktop Services Section - Full Width */}
                {!isMobileView && isServicesOpen && (
                    <div
                        className="w-full bg-white shadow-lg z-40 border-t border-gray-200"
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                    >
                        <div className="w-full px-4 py-8">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                {serviceLinks.map((service, index) => (
                                    <HashLink
                                        key={index}
                                        to={service.to}
                                        smooth
                                        className="group block p-6 rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-200 flex-shrink-0 bg-white text-center"
                                        onClick={() => {
                                            setIsServicesOpen(false);
                                        }}
                                    >
                                        <h3 className="font-bold text-gray-900 group-hover:text-primary text-xl whitespace-normal break-words">
                                            {service.name}
                                        </h3>
                                    </HashLink>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Add padding to body content to account for fixed navbar and sections */}
            <div className="pt-16"></div>
        </>
    );
};

export default Navbar;